import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator } from '@/components/ui/input-otp';

export default function Login() {
  const [email, setEmail] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const { sendOtp, verifyOtp } = useAuth();
  const navigate = useNavigate();

  const handleSendOtp = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Enter a valid email address');
      return;
    }
    setSending(true);
    setError('');
    const result = await sendOtp(trimmed);
    setSending(false);
    if (result.error) { setError(result.error); return; }
    setOtpSent(true);
  };

  const handleVerify = async () => {
    setVerifying(true);
    setError('');
    const result = await verifyOtp(email.trim().toLowerCase(), otp);
    setVerifying(false);
    if (result.error) { setError(result.error); return; }
    navigate(result.isNew ? '/onboarding' : '/');
  };

  return (
    <div className="h-[100dvh] flex flex-col bg-[#F8F2F8]">
      <div className="bg-[radial-gradient(circle_at_top,_#FEE4EE_0%,_#E08AC4_40%,_#AA4F97_72%,_#7B317D_100%)] flex flex-col items-center justify-center px-6 min-h-[35vh]">
        <img
          src="/jiv-logo.png"
          alt="Jiv"
          className="w-28 h-28 mb-3 rounded-3xl object-contain shadow-[0_10px_30px_rgba(0,0,0,0.2)]"
        />
        <p className="text-white/90 mt-1 text-center text-sm font-medium">Where the Jain community finds connection</p>
      </div>

      <div className="bg-[#FFF7FC] px-6 py-8 rounded-t-[24px] -mt-6 relative z-10 animate-fade-in flex-1 overflow-auto pb-24 shadow-[0_-10px_30px_rgba(123,49,125,0.18)]">
        <h2 className="text-xl font-semibold text-[#6B2A6E] mb-6" data-testid="login-heading">
          {otpSent ? 'Enter verification code' : 'Log in or sign up'}
        </h2>

        {!otpSent ? (
          <div className="space-y-4">
            <div>
              <label className="text-sm text-[#8A6D86] mb-1.5 block">Email address</label>
              <Input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSendOtp()}
                className="h-12 text-base rounded-xl border-[#E7C6DF] bg-white focus-visible:ring-[#C857A8]"
                data-testid="email-input"
              />
            </div>
            <Button onClick={handleSendOtp} disabled={sending} className="w-full h-12 text-base rounded-xl bg-[#B64A98] hover:bg-[#A33E88] text-white" data-testid="send-otp-btn">
              {sending ? 'Sending...' : 'Send code'}
            </Button>
            {error && <p className="text-destructive text-sm" data-testid="login-error">{error}</p>}
          </div>
        ) : (
          <div className="space-y-4 animate-fade-in">
            <p className="text-sm text-[#8A6D86]">We sent a code to {email}</p>
            <div className="flex justify-center" data-testid="otp-input">
              <InputOTP maxLength={6} value={otp} onChange={setOtp}>
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                </InputOTPGroup>
                <InputOTPSeparator />
                <InputOTPGroup>
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>
            <Button onClick={handleVerify} className="w-full h-12 text-base rounded-xl bg-[#B64A98] hover:bg-[#A33E88] text-white" disabled={otp.length < 6 || verifying} data-testid="verify-otp-btn">
              {verifying ? 'Verifying...' : 'Verify'}
            </Button>
            <button onClick={() => { setOtpSent(false); setOtp(''); setError(''); }} className="text-sm text-[#9B3F85] w-full text-center" data-testid="change-email-btn">
              Change email
            </button>
            {error && <p className="text-destructive text-sm">{error}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
